/**
 * Two-level product category hierarchy (migration 193): a category with
 * `parentId` is a subcategory of that main category. The backend
 * (productCategoryService) owns the rules; this module only shapes the list
 * for display and is the single home for the "Kategoria / Podkategoria" label.
 */

export interface HierarchyCategory {
  id: string;
  name: string;
  parentId?: string | null;
}

export type CategoryTreeNode<T extends HierarchyCategory> = T & { children?: CategoryTreeNode<T>[] };

const byName = (left: HierarchyCategory, right: HierarchyCategory) => left.name.localeCompare(right.name, 'pl');

export const CATEGORY_PATH_SEPARATOR = ' / ';

/** "Płyty cementowe / Fibrex" for a subcategory, the plain name otherwise. */
export function formatCategoryPath(name?: string | null, parentName?: string | null): string {
  if (!name) return '';
  return parentName ? `${parentName}${CATEGORY_PATH_SEPARATOR}${name}` : name;
}

/**
 * Main categories sorted by name, each with its subcategories as `children`
 * (omitted when empty, so tables show no expand icon). A subcategory whose
 * parent is missing from the list is shown as a main category.
 */
export function buildCategoryTree<T extends HierarchyCategory>(categories: T[]): CategoryTreeNode<T>[] {
  const ids = new Set(categories.map((category) => category.id));
  const childrenByParent = new Map<string, T[]>();
  const roots: T[] = [];
  categories.forEach((category) => {
    if (category.parentId && ids.has(category.parentId)) {
      const siblings = childrenByParent.get(category.parentId) || [];
      siblings.push(category);
      childrenByParent.set(category.parentId, siblings);
    } else {
      roots.push(category);
    }
  });
  return [...roots].sort(byName).map((root) => {
    const children = (childrenByParent.get(root.id) || []).sort(byName);
    return children.length > 0 ? { ...root, children } : { ...root };
  });
}

/** Main categories first, each followed by its subcategories (for flat selects). */
export function flattenCategoryTree<T extends HierarchyCategory>(categories: T[]): T[] {
  return buildCategoryTree(categories).flatMap(({ children, ...root }) => [
    root as unknown as T,
    ...((children || []) as unknown as T[]),
  ]);
}

/** The main category id a category belongs to (itself for a main category). */
export function rootCategoryId(category: HierarchyCategory): string {
  return category.parentId || category.id;
}
